/**
 * PURPOSE: Barra flotante de acciones masivas del Centro de Programación (FTR-0028 WG3, §17).
 * RESPONSIBILITIES: Aplicar fecha, hora, sede, cancha o una cadena de horarios a la selección, y
 *   guardar la edición inline pendiente. Es la misma barra en las cuatro vistas.
 *
 *   NUNCA UN BYE (D-20). La selección ya los excluye en el proveedor, así que la barra no tiene que
 *   filtrarlos otra vez: la regla vive en un solo sitio.
 *
 *   NADA SE PINTA ANTES DE CONFIRMARSE. Un lote puede quedar parcialmente rechazado por propiedad,
 *   actividad o una sede inválida. Se envía, se espera y se muestra el recuento real que devolvió el
 *   servidor ("17 actualizados · 2 ya materializados"), no una suposición.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, utils/programmingCenterEdit.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/SelectionActionBar.jsx
 */
import React, { useState } from "react";
import { FiCheck, FiSave, FiX, FiZap } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { assignableCourts } from "../../../../utils/programmingCenterEdit.js";

const inputClass =
  "rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const EMPTY = { date: "", time: "", idsede: "", idcancha: "" };

const SelectionActionBar = () => {
  const { selection, selectedRows, clearSelection, dirtyEntries, actions, busy, context } =
    useProgrammingCenter();
  const [changes, setChanges] = useState(EMPTY);
  const [chain, setChain] = useState({ startTime: "", stepMinutes: "45" });

  const hasSelection = selection.size > 0;
  const hasDirty = dirtyEntries.length > 0;
  if (!hasSelection && !hasDirty) return null;

  const courts = changes.idsede ? assignableCourts(context, changes.idsede) : [];
  const working = Boolean(busy);

  const applyBulk = async () => {
    const payload = {};
    for (const field of ["date", "time", "idsede", "idcancha"]) {
      if (changes[field] !== "") payload[field] = changes[field];
    }
    const result = await actions.applyBulk(payload);
    if (result?.ok) setChanges(EMPTY);
  };

  /** Limpiar un campo es una intención explícita: viaja como "" y el backend lo guarda NULL. */
  const clearField = async (field) => {
    const payload = { [field]: "" };
    await actions.applyBulk(payload);
  };

  return (
    <div className="sticky bottom-0 z-20 flex flex-wrap items-end gap-2 border-t border-blue-200 bg-blue-50/95 px-4 py-3 shadow-[0_-4px_12px_rgba(15,23,42,0.06)] backdrop-blur print:hidden">
      {hasSelection ? (
        <>
          <span className="flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-black text-white">
            {selection.size} seleccionados
            <button
              type="button"
              onClick={clearSelection}
              aria-label="Limpiar selección"
              className="rounded-full p-0.5 transition hover:bg-blue-700"
            >
              <FiX className="text-sm" />
            </button>
          </span>

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">Fecha</span>
            <input
              type="date"
              value={changes.date}
              onChange={(event) => setChanges((c) => ({ ...c, date: event.target.value }))}
              className={inputClass}
              aria-label="Fecha para la selección"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">Hora</span>
            <input
              type="time"
              value={changes.time}
              onChange={(event) => setChanges((c) => ({ ...c, time: event.target.value }))}
              className={inputClass}
              aria-label="Hora para la selección"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">Sede</span>
            <select
              value={changes.idsede}
              onChange={(event) =>
                setChanges((c) => ({ ...c, idsede: event.target.value, idcancha: "" }))
              }
              className={inputClass}
              aria-label="Sede para la selección"
            >
              <option value="">Sin cambio</option>
              {(context?.venues || []).map((venue) => (
                <option key={venue.idsede} value={venue.idsede}>
                  {venue.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">Cancha</span>
            <select
              value={changes.idcancha}
              onChange={(event) => setChanges((c) => ({ ...c, idcancha: event.target.value }))}
              disabled={!changes.idsede}
              className={`${inputClass} disabled:bg-slate-100 disabled:text-slate-400`}
              aria-label="Cancha para la selección"
            >
              <option value="">Sin cambio</option>
              {courts.map((court) => (
                <option key={court.idcancha} value={court.idcancha}>
                  {court.name}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={applyBulk}
            disabled={working}
            className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiCheck className="text-sm" />
            Aplicar a {selection.size}
          </button>

          <span className="mx-1 h-8 w-px bg-blue-200" aria-hidden="true" />

          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">
              Encadenar desde
            </span>
            <input
              type="time"
              value={chain.startTime}
              onChange={(event) => setChain((c) => ({ ...c, startTime: event.target.value }))}
              className={inputClass}
              aria-label="Hora inicial de la cadena"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] font-black uppercase tracking-wide text-slate-500">
              Cada (min)
            </span>
            <input
              type="number"
              min="0"
              step="5"
              value={chain.stepMinutes}
              onChange={(event) => setChain((c) => ({ ...c, stepMinutes: event.target.value }))}
              className={`${inputClass} w-20`}
              aria-label="Intervalo en minutos"
            />
          </label>
          <button
            type="button"
            onClick={() => actions.applyChainedTime(chain)}
            disabled={working}
            title="Asigna horarios consecutivos a los partidos seleccionados, en su orden de pantalla"
            className="flex items-center gap-1 rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-black text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiZap className="text-sm" />
            Encadenar
          </button>

          <button
            type="button"
            onClick={() => clearField("idsede")}
            disabled={working}
            title="Quitar la sede (y su cancha) de los partidos seleccionados"
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-600 transition hover:border-rose-300 hover:text-rose-600 disabled:opacity-50"
          >
            Quitar sede
          </button>
        </>
      ) : null}

      {hasDirty ? (
        <button
          type="button"
          onClick={() => actions.saveInlineChanges()}
          disabled={working}
          className="ml-auto flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-black text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiSave className="text-sm" />
          Guardar {dirtyEntries.length} {dirtyEntries.length === 1 ? "cambio" : "cambios"}
        </button>
      ) : null}

      {hasSelection && selectedRows.length !== selection.size ? (
        <span className="w-full text-[11px] font-bold text-slate-500">
          {selection.size - selectedRows.length} partidos seleccionados están fuera del filtro actual
          y también recibirán el cambio.
        </span>
      ) : null}
    </div>
  );
};

export default SelectionActionBar;
